/**
 * Save Search Modal Component
 * KOPS-343: Capture user information when they're ready to view properties
 * 
 * Appears at the optimal engagement point when users have refined their
 * search to a manageable number of properties and click "View Properties"
 */

import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Badge, ListGroup, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faChartLine,
  faStar,
  faPhone,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';

interface SaveSearchModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: SaveSearchData) => void;
  searchCriteria: any;
  matchCount: number;
}

interface SaveSearchData {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  alertFrequency: 'instant' | 'daily' | 'weekly';
  agreeToTerms: boolean;
}

const SaveSearchModal: React.FC<SaveSearchModalProps> = ({
  open,
  onClose,
  onSave,
  searchCriteria,
  matchCount
}) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [alertFrequency, setAlertFrequency] = useState<'instant' | 'daily' | 'weekly'>('instant');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; firstName?: string; terms?: string }>({});

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = () => {
    const newErrors: { email?: string; firstName?: string; terms?: string } = {};
    
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!firstName) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!agreeToTerms) {
      newErrors.terms = 'You must agree to receive property alerts';
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length === 0) {
      onSave({
        email,
        firstName: firstName || undefined,
        lastName: lastName || undefined,
        phone: phone || undefined,
        alertFrequency,
        agreeToTerms
      });
    }
  };

  const getSearchSummary = () => {
    const parts = [];
    if (searchCriteria.city) parts.push(searchCriteria.city);
    if (searchCriteria.beds) parts.push(`${searchCriteria.beds}+ beds`);
    if (searchCriteria.maxPrice) parts.push(`Under $${(searchCriteria.maxPrice / 1000).toFixed(0)}K`);
    if (searchCriteria.features?.includes('pool')) parts.push('Pool');
    return parts.join(' • ') || 'Your custom search';
  };

  return (
    <Modal show={open} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <h4 className="mb-0">Save Your Perfect Search</h4>
          <small className="text-muted d-block mt-1">
            {matchCount} properties match: {getSearchSummary()}
          </small>
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {/* Benefits Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">Why save this search?</h6>
          <ListGroup variant="flush">
            <ListGroup.Item className="px-0 py-2 bg-transparent">
              <FontAwesomeIcon icon={faBell} className="text-primary me-3" />
              <span>Get instant alerts for new matches</span>
            </ListGroup.Item>
            <ListGroup.Item className="px-0 py-2 bg-transparent">
              <FontAwesomeIcon icon={faChartLine} className="text-primary me-3" />
              <span>Price drop notifications</span>
            </ListGroup.Item>
            <ListGroup.Item className="px-0 py-2 bg-transparent">
              <FontAwesomeIcon icon={faStar} className="text-primary me-3" />
              <span>Be first to see new listings</span>
            </ListGroup.Item>
          </ListGroup>
        </div>

        {/* Form Fields */}
        <Form>
          {/* Name Fields */}
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  isInvalid={!!errors.firstName}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <div className="input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faEnvelope} />
              </span>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                isInvalid={!!errors.email}
                required
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </div>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Phone (optional)</Form.Label>
            <div className="input-group">
              <span className="input-group-text">
                <FontAwesomeIcon icon={faPhone} />
              </span>
              <Form.Control
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
            <Form.Text className="text-muted">
              Get text alerts for urgent updates
            </Form.Text>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Alert Frequency</Form.Label>
            <Form.Select
              value={alertFrequency}
              onChange={(e) => setAlertFrequency(e.target.value as 'instant' | 'daily' | 'weekly')}
            >
              <option value="instant">Instant - As soon as properties match</option>
              <option value="daily">Daily Summary - Once per day</option>
              <option value="weekly">Weekly Digest - Once per week</option>
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="terms-checkbox"
              label="I agree to receive property alerts and updates about my saved search"
              checked={agreeToTerms}
              onChange={(e) => setAgreeToTerms(e.target.checked)}
              isInvalid={!!errors.terms}
            />
            {errors.terms && (
              <div className="invalid-feedback d-block">
                {errors.terms}
              </div>
            )}
          </Form.Group>
        </Form>

        <Alert variant="info" className="mt-3">
          <small>
            We'll create a free account for you to manage your saved searches and view full property details.
          </small>
        </Alert>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="link" onClick={onClose}>
          Skip for now
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Save Search & View Properties
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SaveSearchModal;